import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ConnectProvider, ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { HTTP_CLIENT } from './token.service';
import { LoggerService } from './logger.service';

// Reads connect-provider resources from the customer-connect service (io.netfoundry.connect),
// served at environment.customerConnectUrl (e.g. https://gateway.<env>.netfoundry.io/customer-connect/v1/).
// CustomerConnect is its own resource type, distinct from Customer.
@Injectable({ providedIn: 'root' })
export class ConnectProviderService {
    apiUrl;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = `${this.environment.customerConnectUrl}providers`;
    }

    // Looks up specific providers by id, in one request (the endpoint's `id` filter takes a set).
    // Callers pass only the ids they need to resolve - an organization or network group has at most one
    // provider, while a super-user's full provider list would be large and almost entirely irrelevant.
    public findConnectProvidersByIds(ids: string[]): Promise<ConnectProvider[]> {
        const wantedIds = (ids || []).filter((id) => !!id);
        if (!wantedIds.length) {
            return Promise.resolve([] as ConnectProvider[]);
        }
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');
        const params = new HttpParams()
            .set('id', wantedIds.join(','))
            .set('page', '0')
            .set('size', `${wantedIds.length}`);
        return this.http
            .get(this.apiUrl, { headers, params })
            .toPromise()
            .then((result: any) => this.extractConnectProviders(result))
            .catch((error: HttpErrorResponse) => {
                this.logger.error(`Connect-provider request failed with code ${error?.status}`, error?.error);
                return [] as ConnectProvider[];
            });
    }

    // the connect-provider list is a HAL collection with the items under _embedded.providerList
    private extractConnectProviders(result: any): ConnectProvider[] {
        if (!result) {
            return [];
        }
        if (Array.isArray(result)) {
            return result as ConnectProvider[];
        }
        const embedded = result._embedded;
        if (embedded) {
            if (Array.isArray(embedded.providerList)) {
                return embedded.providerList as ConnectProvider[];
            }
            const collection = Object.values(embedded).find((value) => Array.isArray(value));
            if (collection) {
                return collection as ConnectProvider[];
            }
        }
        return [];
    }
}
